import { useEffect, useState } from "react";

/**
 * PWA "Add to Home Screen" prompt.
 *
 * Android Chrome fires a `beforeinstallprompt` event we can capture and
 * show as a custom button. iOS Safari has no such API — users must go
 * Share → Add to Home Screen manually, so we show a short instructions
 * card instead.
 *
 * The prompt auto-hides when:
 *   - The app is already running installed (display-mode: standalone / iOS navigator.standalone)
 *   - The user dismisses it (remembered in localStorage for 30 days)
 *   - The user is inside the Capacitor native shell (already an app)
 */
export const InstallPWAPrompt = () => {
    const [deferredPrompt, setDeferredPrompt] = useState(null);
    const [platform, setPlatform] = useState("unknown"); // "android" | "ios" | "unknown"
    const [dismissed, setDismissed] = useState(true); // hide by default; opt-in below
    const [installed, setInstalled] = useState(false);

    useEffect(() => {
        // Already installed? Detect standalone display mode + iOS "navigator.standalone".
        const standalone =
            (window.matchMedia && window.matchMedia("(display-mode: standalone)").matches) ||
            window.navigator.standalone === true;
        if (standalone) {
            setInstalled(true);
            return;
        }

        // Inside Capacitor native shell? No install prompt needed.
        if (window.Capacitor?.isNativePlatform?.()) {
            setInstalled(true);
            return;
        }

        // Dismissed within the last 30 days?
        try {
            const raw = localStorage.getItem("naami_pwa_dismissed_at");
            if (raw) {
                const at = parseInt(raw, 10);
                if (Date.now() - at < 30 * 24 * 60 * 60 * 1000) return;
            }
        } catch { /* ignore */ }

        // Detect iOS vs Android from UA
        const ua = navigator.userAgent || "";
        const isIOS = /iPhone|iPad|iPod/i.test(ua) && !window.MSStream;
        const isAndroid = /Android/i.test(ua);
        if (isIOS) {
            setPlatform("ios");
            setDismissed(false); // show iOS instructions immediately
        } else if (isAndroid) {
            setPlatform("android");
            // Only show on Android AFTER Chrome fires beforeinstallprompt.
            const onBeforeInstall = (e) => {
                e.preventDefault();
                setDeferredPrompt(e);
                setDismissed(false);
            };
            window.addEventListener("beforeinstallprompt", onBeforeInstall);
            return () => window.removeEventListener("beforeinstallprompt", onBeforeInstall);
        }
    }, []);

    useEffect(() => {
        const onInstalled = () => {
            setInstalled(true);
            try { localStorage.setItem("naami_pwa_installed", "1"); } catch { /* ignore */ }
        };
        window.addEventListener("appinstalled", onInstalled);
        return () => window.removeEventListener("appinstalled", onInstalled);
    }, []);

    const install = async () => {
        if (!deferredPrompt) return;
        deferredPrompt.prompt();
        const { outcome } = await deferredPrompt.userChoice;
        setDeferredPrompt(null);
        if (outcome === "dismissed") {
            try { localStorage.setItem("naami_pwa_dismissed_at", String(Date.now())); } catch { /* ignore */ }
        }
        setDismissed(true);
    };

    const dismiss = () => {
        try { localStorage.setItem("naami_pwa_dismissed_at", String(Date.now())); } catch { /* ignore */ }
        setDismissed(true);
    };

    if (installed || dismissed) return null;

    // Android — native install prompt available
    if (platform === "android" && deferredPrompt) {
        return (
            <div
                className="fixed bottom-4 inset-x-4 md:inset-x-auto md:right-4 md:w-[340px] z-50 border border-[var(--gold)]/60 bg-[var(--bg)] shadow-xl p-4"
                data-testid="pwa-install-prompt-android"
            >
                <button
                    onClick={dismiss}
                    aria-label="Dismiss"
                    className="absolute top-2 right-2 font-mono text-[12px] text-muted hover:text-gold"
                    data-testid="pwa-install-dismiss"
                >×</button>
                <p className="font-mono text-[9px] uppercase tracking-[0.22em] text-gold mb-1">// Install App</p>
                <p className="font-display text-[16px] mb-1">Add Naami to your phone</p>
                <p className="text-[12px] text-muted mb-3">One-tap ordering, no browser. Get order-ready alerts.</p>
                <button
                    onClick={install}
                    className="btn-primary w-full justify-center text-[11px] font-mono uppercase tracking-[0.16em]"
                    data-testid="pwa-install-cta"
                >
                    Install →
                </button>
            </div>
        );
    }

    // iOS — no API, show step-by-step
    if (platform === "ios") {
        return (
            <div
                className="fixed bottom-4 inset-x-4 md:inset-x-auto md:right-4 md:w-[340px] z-50 border border-[var(--gold)]/60 bg-[var(--bg)] shadow-xl p-4"
                data-testid="pwa-install-prompt-ios"
            >
                <button
                    onClick={dismiss}
                    aria-label="Dismiss"
                    className="absolute top-2 right-2 font-mono text-[12px] text-muted hover:text-gold"
                    data-testid="pwa-install-dismiss"
                >×</button>
                <p className="font-mono text-[9px] uppercase tracking-[0.22em] text-gold mb-1">// Install App</p>
                <p className="font-display text-[16px] mb-2">Add Naami to your Home Screen</p>
                <ol className="text-[12px] text-muted space-y-1 mb-2 list-decimal ml-4">
                    <li>Tap the <span className="text-gold">Share</span> button below</li>
                    <li>Scroll and tap <span className="text-gold">Add to Home Screen</span></li>
                    <li>Tap <span className="text-gold">Add</span> — done!</li>
                </ol>
                <p className="text-[10px] text-muted italic">Then open the Naami icon like any other app.</p>
            </div>
        );
    }

    return null;
};
